import React from 'react'

interface CrystalBallProps {
  size?: number
  className?: string
  animated?: boolean
  glowing?: boolean
}

const CrystalBall: React.FC<CrystalBallProps> = ({ 
  size = 120, 
  className = '', 
  animated = true,
  glowing = true 
}) => {
  return (
    <div className={className} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="ballGradient" cx="40%" cy="30%" r="60%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="30%" stopColor="rgba(200,220,255,0.8)" />
            <stop offset="70%" stopColor="rgba(150,180,255,0.9)" />
            <stop offset="100%" stopColor="rgba(100,150,255,1)" />
          </radialGradient>
          
          <radialGradient id="baseGradient" cx="50%" cy="20%" r="80%">
            <stop offset="0%" stopColor="#ffd700" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#d97706" />
          </radialGradient>

          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          <pattern id="mysticalPattern" patternUnits="objectBoundingBox" width="0.2" height="0.2">
            <circle cx="0.1" cy="0.1" r="0.02" fill="rgba(255,255,255,0.3)">
              {animated && (
                <animate attributeName="opacity" values="0.1;0.8;0.1" dur="4s" repeatCount="indefinite" />
              )}
            </circle>
          </pattern>
        </defs>

        {/* Ornate Base */}
        <ellipse cx="60" cy="105" rx="45" ry="12" fill="url(#baseGradient)" />
        <ellipse cx="60" cy="100" rx="40" ry="8" fill="url(#baseGradient)" />
        
        {/* Base Details */}
        <rect x="25" y="98" width="70" height="4" rx="2" fill="#b45309" />
        <circle cx="35" cy="100" r="3" fill="#fbbf24" />
        <circle cx="60" cy="100" r="3" fill="#fbbf24" />
        <circle cx="85" cy="100" r="3" fill="#fbbf24" />

        {/* Main Crystal Ball */}
        <circle 
          cx="60" 
          cy="55" 
          r="40" 
          fill="url(#ballGradient)" 
          filter={glowing ? "url(#glow)" : "none"}
        >
          {animated && (
            <animate attributeName="r" values="40;42;40" dur="6s" repeatCount="indefinite" />
          )}
        </circle>

        {/* Mystical Pattern Inside */}
        <circle cx="60" cy="55" r="35" fill="url(#mysticalPattern)" opacity="0.6" />

        {/* Highlight */}
        <ellipse cx="50" cy="40" rx="12" ry="18" fill="rgba(255,255,255,0.4)" />
        <ellipse cx="48" cy="38" rx="6" ry="10" fill="rgba(255,255,255,0.6)" />

        {/* Inner Swirls */}
        <path 
          d="M 40 45 Q 60 30 80 50 Q 70 70 50 65 Q 35 55 40 45" 
          fill="none" 
          stroke="rgba(255,255,255,0.3)" 
          strokeWidth="1"
        >
          {animated && (
            <animate 
              attributeName="stroke-dasharray" 
              values="0,100;50,50;100,0" 
              dur="8s" 
              repeatCount="indefinite" 
            />
          )}
        </path>

        {/* Mystical Symbols Floating Around */}
        <g className="floating-symbols">
          <text x="25" y="25" fontSize="12" fill="#ffd700" opacity="0.7">✨</text>
          <text x="90" y="30" fontSize="10" fill="#ec4899" opacity="0.6">🌙</text>
          <text x="100" y="70" fontSize="14" fill="#3b82f6" opacity="0.8">⭐</text>
          <text x="15" y="80" fontSize="11" fill="#10b981" opacity="0.7">🔮</text>
          
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 55;360 60 55"
              dur="20s"
              repeatCount="indefinite"
            />
          )}
        </g>

        {/* Energy Waves */}
        {animated && (
          <g>
            <circle cx="60" cy="55" r="45" fill="none" stroke="rgba(102,126,234,0.3)" strokeWidth="1">
              <animate attributeName="r" values="40;60;40" dur="4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.5;0;0.5" dur="4s" repeatCount="indefinite" />
            </circle>
            <circle cx="60" cy="55" r="50" fill="none" stroke="rgba(236,72,153,0.2)" strokeWidth="1">
              <animate attributeName="r" values="45;65;45" dur="5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.3;0;0.3" dur="5s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
      </svg>
    </div>
  )
}

export default CrystalBall
